import React from 'react';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';

export default function ErrorLayout({ children, errorCode }) {
  const [shouldAnimate, setShouldAnimate] = React.useState(true);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setShouldAnimate(!mediaQuery.matches);
    
    const handler = (e) => setShouldAnimate(!e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const particles = Array.from({ length: 15 }).map((_, i) => ({
    id: i,
    size: Math.random() * 6 + 2,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 5,
    duration: Math.random() * 15 + 10,
  }));

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: '#07090e',
        color: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 4 },
        fontFamily: '"Plus Jakarta Sans", "Roboto", "Helvetica", "Arial", sans-serif',
      }}
    >
      {/* 1. Neon Glowing Orbs */}
      <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <motion.div
          animate={shouldAnimate ? {
            scale: [1, 1.2, 1],
            x: [0, 40, 0],
            y: [0, -20, 0],
          } : {}}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: 'absolute',
            top: '-10%',
            left: '-10%',
            width: '50vw',
            height: '50vw',
            borderRadius: '50%',
            backgroundColor: 'rgba(6, 182, 212, 0.08)',
            filter: 'blur(120px)',
          }}
        />
        <motion.div
          animate={shouldAnimate ? {
            scale: [1.1, 0.9, 1.1],
            x: [0, -30, 0],
            y: [0, 40, 0],
          } : {}}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          style={{
            position: 'absolute',
            bottom: '-10%',
            right: '-10%',
            width: '50vw',
            height: '50vw',
            borderRadius: '50%',
            backgroundColor: 'rgba(37, 99, 235, 0.08)',
            filter: 'blur(120px)',
          }}
        />
      </Box>

      {/* 2. Cyber Space Grid */}
      <Box 
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(to right, rgba(30, 41, 59, 0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(30, 41, 59, 0.06) 1px, transparent 1px)',
          backgroundSize: '4rem 4rem',
          maskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, #000 70%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* 3. Floating Code Particles */}
      {shouldAnimate && (
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {particles.map((p) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0.1, y: `${p.y}%`, x: `${p.x}%` }}
              animate={{
                y: [`${p.y}%`, `${p.y - 15}%`, `${p.y}%`],
                opacity: [0.1, 0.4, 0.1],
              }}
              transition={{
                duration: p.duration,
                repeat: Infinity,
                delay: p.delay,
                ease: "easeInOut",
              }}
              style={{
                position: 'absolute',
                width: p.size,
                height: p.size,
                borderRadius: '50%',
                backgroundColor: 'rgba(34, 211, 238, 0.25)',
                filter: 'blur(1px)',
              }}
            />
          ))}
        </Box>
      )}

      {/* 4. Large Faded Error Code Watermark */}
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          userSelect: 'none',
          pointerEvents: 'none',
          fontSize: '15vw',
          fontWeight: 900,
          color: 'rgba(30, 41, 59, 0.04)',
          fontFamily: 'monospace',
          letterSpacing: '-0.05em',
        }}
      >
        {errorCode}
      </Box>

      {/* 5. Main Content Wrapper */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: '1200px',
          marginLeft: 'auto',
          marginRight: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {children}
      </motion.div>
    </Box>
  );
}
